import { api } from './api';
import type { PublicBranding } from './types';

export const DEFAULT_BRANDING: PublicBranding = {
  schemaVersion: 1,
  customized: false,
  productName: 'DeweStream',
  shortName: 'DeweStream',
  operatorName: 'dewe.stream',
  tagline: 'Secure, Flexible, and Affordable Video Hosting for Everyone',
  colors: {
    primary: '#5A47D7',
    primaryHover: '#4936C6',
    primarySoft: '#F0EDFF',
  },
  assets: {
    wordmark: '/branding/dewestream-wordmark.png',
    favicon: '/branding/favicon.ico',
    icon192: '/branding/icon-192.png',
    icon512: '/branding/icon-512.png',
    maskableIcon512: '/branding/icon-maskable-512.png',
    appleTouchIcon: '/branding/apple-touch-icon.png',
    loginIllustration: '/branding/login-illustration.webp',
    socialImage: '/branding/social-og-image.webp',
  },
  links: {
    homepage: 'https://dewe.stream/',
    privacy: null,
    terms: null,
    supportEmail: null,
  },
};

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function validText(value: unknown, max: number): value is string {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= max;
}

function validColor(value: unknown): value is string {
  return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);
}

function validAsset(value: unknown): value is string {
  if (typeof value !== 'string' || value.includes('..')) return false;
  const pathAllowed = (path: string) => /^\/(?:branding|branding-runtime|api\/public-branding)\/[A-Za-z0-9._/-]+$/.test(path);
  if (pathAllowed(value)) return true;
  const base = typeof document !== 'undefined' ? document.querySelector<HTMLMetaElement>('meta[name="dewe-public-api-base"]')?.content : null;
  if (!base) return false;
  try { const url = new URL(value); return url.origin === new URL(base).origin && pathAllowed(url.pathname) && !url.username && !url.password && !url.hash && !url.search; } catch { return false; }
}

function validLink(value: unknown): value is string | null {
  if (value === null) return true;
  if (typeof value !== 'string') return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' && !parsed.username && !parsed.password;
  } catch {
    return false;
  }
}

export function validBranding(value: unknown): value is PublicBranding {
  const source = record(value);
  const colors = record(source?.colors);
  const assets = record(source?.assets);
  const links = record(source?.links);
  const surface = record(source?.publicSurface);
  return source?.schemaVersion === 1 && typeof source.customized === 'boolean'
    && (source.publicSurface === undefined || !!surface && typeof surface.playerLogo === 'boolean' && typeof surface.hidePoweredBy === 'boolean')
    && validText(source.productName, 48) && validText(source.shortName, 24)
    && validText(source.operatorName, 80) && validText(source.tagline, 160)
    && validColor(colors?.primary) && validColor(colors?.primaryHover) && validColor(colors?.primarySoft)
    && validAsset(assets?.wordmark) && validAsset(assets?.favicon)
    && validAsset(assets?.icon192) && validAsset(assets?.icon512)
    && validAsset(assets?.maskableIcon512) && validAsset(assets?.appleTouchIcon)
    && validAsset(assets?.loginIllustration) && validAsset(assets?.socialImage)
    && validLink(links?.homepage) && validLink(links?.privacy) && validLink(links?.terms)
    && (links?.supportEmail === null || typeof links?.supportEmail === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(links.supportEmail));
}

function embeddedBranding(): PublicBranding | null {
  if (typeof document === 'undefined') return null;
  const encoded = document.querySelector<HTMLMetaElement>('meta[name="dewe-installation-branding"]')?.content.trim();
  if (!encoded) return null;
  try {
    const base64 = encoded.replaceAll('-', '+').replaceAll('_', '/').padEnd(Math.ceil(encoded.length / 4) * 4, '=');
    const bytes = Uint8Array.from(window.atob(base64), (character) => character.charCodeAt(0));
    const parsed: unknown = JSON.parse(new TextDecoder().decode(bytes));
    return validBranding(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

let activeBranding = embeddedBranding() ?? DEFAULT_BRANDING;

function hexRgb(value: string): string {
  return [value.slice(1, 3), value.slice(3, 5), value.slice(5, 7)]
    .map((channel) => Number.parseInt(channel, 16))
    .join(' ');
}

function setMeta(marker: string, value: string): void {
  document.querySelector<HTMLMetaElement>(`meta[data-brand-field="${marker}"]`)?.setAttribute('content', value);
}

function setLink(marker: string, value: string): void {
  document.querySelector<HTMLLinkElement>(`link[data-brand-field="${marker}"]`)?.setAttribute('href', value);
}

export function applyBranding(branding: PublicBranding, options: { metadata?: boolean } = {}): void {
  activeBranding = branding;
  const style = document.documentElement.style;
  style.setProperty('--brand', branding.colors.primary);
  style.setProperty('--brand-dark', branding.colors.primaryHover);
  style.setProperty('--brand-soft', branding.colors.primarySoft);
  style.setProperty('--brand-rgb', hexRgb(branding.colors.primary));
  setMeta('theme-color', branding.colors.primary);
  setMeta('application-name', branding.productName);
  setMeta('apple-title', branding.shortName);
  if (options.metadata !== false) {
    document.title = branding.productName;
    setMeta('description', branding.tagline);
    setMeta('og-site-name', branding.productName);
    setMeta('og-title', branding.productName);
    setMeta('og-description', branding.tagline);
    setMeta('og-image', branding.assets.socialImage);
    setMeta('twitter-title', branding.productName);
    setMeta('twitter-description', branding.tagline);
    setMeta('twitter-image', branding.assets.socialImage);
  }
  setLink('favicon', branding.assets.favicon);
  setLink('favicon-png', branding.assets.icon192);
  setLink('apple-touch-icon', branding.assets.appleTouchIcon);
}

export async function initializeBranding(): Promise<PublicBranding> {
  const fallbackBranding = embeddedBranding() ?? DEFAULT_BRANDING;
  applyBranding(fallbackBranding);
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 3_000);
  try {
    const branding = await api.branding(controller.signal);
    applyBranding(validBranding(branding) ? branding : DEFAULT_BRANDING);
  } catch {
    applyBranding(fallbackBranding);
  } finally {
    window.clearTimeout(timeout);
  }
  return activeBranding;
}

export function getBranding(): PublicBranding {
  return activeBranding;
}

export function brandingFilenamePrefix(): string {
  return activeBranding.shortName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'video-hosting';
}

export function resetBrandingForTests(): void {
  applyBranding(DEFAULT_BRANDING);
}
